/* global window */
import { parse } from 'mini-querystring';
import PropTypes from 'prop-types';
import React, { Component } from 'react';

import { injectClient } from 'shared/src/components/contexts/ClientContext';
import WaitFor from '../../data/WaitFor';

import { makeToast } from '../toasts';
import SitePermissionDenied from './SitePermissionDenied';
import SitePermissionRedirect from './SitePermissionRedirect';

const parseQuery = str => parse(str.replace(/^[?#]/, ''));

const getRedirectPath = () => {
  const stateJson = new URL(window.location).searchParams.get('state');
  try {
    const state = JSON.parse(stateJson);
    return state.redirect_to;
  } catch (err) {
    // Ignore parse error and return default
  }
  return '';
};

const getError = () => parseQuery(window.location.search).error;
const getErrorDescription = () => {
  try {
    return parseQuery(window.location.search).error_description.replace(/\+/g, ' ');
  } catch (err) {
    return '';
  }
};

const IGNORE_PATHS = [
  '/auth',
  '/apps',
];

class AuthReceiver extends Component {
  state = {
    authenticating: false,
    permissionError: null,
  };

  authenticate() {
    const { client, sitePermission, redirectToOwnerDashboard } = this.props;
    const code = new URL(window.location).searchParams.get('code');

    this.setState({ authenticating: true });
    client.auth.oauthSetCode(code)
      .then(async () => {
        try {
          const skipAccessCheck = window.localStorage.getItem('lmg:SkipAccessCheck')
            || (await client.access.checkPermissions({}, ['*user.admin.access']));
          if (skipAccessCheck) {
            return this.setState({ authenticating: false, authenticated: true });
          }
        } catch {
          // ok, I guess we check
        }

        // HACK: Gross way to check Local Owner Dashboard permission on login _before_ checking anything else...
        if (redirectToOwnerDashboard) {
          const isLocalOwner = await client.access.checkPermissions({}, ['*user.owner.access']);
          if (isLocalOwner) {
            this.setState({
              isLocalOwner: true,
            });
            return client.auth.logout();
          }
        }

        if (!sitePermission) return this.setState({ authenticating: false, authenticated: true });

        const hasPermission = (await client.access.checkPermissions({}, [sitePermission]))
          || (await client.access.checkPermissions({}, ['*user.admin.access']));
        if (!hasPermission) {
          this.setState({
            authenticating: true,
            authenticated: false,
            permissionError: 'User does not have permissions to access this site',
          });

          return client.auth.logout();
        }

        return this.setState({ authenticating: false, authenticated: true });
      });

    return null;
  }

  render() {
    const { children } = this.props;

    if (IGNORE_PATHS.find(route => window.location.pathname.indexOf(route) === 0)) return children;

    const url = new URL(window.location);
    const code = url.searchParams.get('code');
    const redirectTo = getRedirectPath();
    const error = url.searchParams.get('error');
    const errorDescription = url.searchParams.get('error_description');

    if (this.state.isLocalOwner) {
      return <SitePermissionRedirect />;
    }

    if (this.state.permissionError) {
      return <SitePermissionDenied />;
    }

    if (error) {
      makeToast({
        title: 'Login Error',
        message: `${error}: ${errorDescription}`,
        type: 'negative',
        icon: 'lock',
      });
    }

    if (!code) {
      return children;
    }

    if (this.state.authenticating) {
      return <WaitFor />;
    }

    if (!this.state.authenticated) {
      return this.authenticate();
    }

    if (redirectTo) {
      window.location.replace(redirectTo);
      return null;
    }

    return children;
  }
}

AuthReceiver.propTypes = {
  client: PropTypes.object,
  sitePermission: PropTypes.string,
  redirectToOwnerDashboard: PropTypes.bool,
};

export default injectClient(AuthReceiver);
